// <ElakaySwiperBlock> -- a Swiper Manager swiper embedded BY REFERENCE in
// any content builder (Page Builder, Shop Builder, Tutorials...). It only
// knows the swiper id: the swiper itself is read (cached) from the public
// API and drawn by the one renderer, <DynamicSwiper>, so a change in the
// Swiper Manager shows up everywhere it is used -- nothing is copied.
//
// On live pages an inactive / deleted swiper simply renders nothing; in an
// editor (`editorMode`) a clear placeholder says why instead.
import React, { useEffect, useState } from "react";
import { FaImages } from "react-icons/fa";
import { elakayAutoText } from "../pageInside/dataElakay";
import { getPublicSwiper } from "../auth/apiDynamicSwipers";
import DynamicSwiper from "./DynamicSwiper";
import "./ElakaySwiperBlock.css";

const text = elakayAutoText;

// `ownerId`: whose page / shop shows it (lets the server hide a Family swiper
// that is no longer shared with that owner's role).
export const useElakaySwiper = (swiperId, ownerId = null) => {
  const key = swiperId ? `${swiperId}:${ownerId || ""}` : null;
  const [state, setState] = useState({ key: null, swiper: null, loading: Boolean(swiperId) });
  useEffect(() => {
    if (!key) {
      setState({ key: null, swiper: null, loading: false });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => (prev.key === key ? prev : { key, swiper: null, loading: true }));
    getPublicSwiper(swiperId, { ownerId }).then((swiper) => {
      if (!cancelled) setState({ key, swiper, loading: false });
    });
    return () => { cancelled = true; };
    // `key` identifies (swiperId, ownerId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state.key === key ? state : { key, swiper: null, loading: Boolean(swiperId) };
};

const Placeholder = ({ children }) => (
  <div className="esb-placeholder" role="note">
    <FaImages aria-hidden="true" />
    <span>{children}</span>
  </div>
);

const ElakaySwiperBlock = ({
  swiperId, swiperName = "", device = "auto", editorMode = false, maxWidth = 0, className = "", label, onNavigate, ownerId = null, eager = false,
}) => {
  const { swiper, loading } = useElakaySwiper(swiperId, ownerId);
  if (!swiperId) {
    return editorMode ? <Placeholder>{text("h_esb_choose", "Elakay Swiper — choose a swiper from the Swiper Library")}</Placeholder> : null;
  }
  if (loading && editorMode) return <Placeholder>{text("h_esb_loading", "Loading the swiper…")}</Placeholder>;
  if (!swiper) {
    return editorMode
      ? <Placeholder>{text("h_esb_unavailable_any", "\"{name}\" is inactive, was deleted or is no longer shared with you: it is hidden on the live page.", { name: swiperName || swiperId })}</Placeholder>
      : null;
  }
  return (
    <div className={`esb ${className}`.trim()} style={maxWidth > 0 ? { maxWidth: `${maxWidth}px` } : undefined} data-elakay-swiper-id={swiperId}>
      <DynamicSwiper swiper={swiper} device={device} label={label} onNavigate={onNavigate} eager={eager} />
    </div>
  );
};

export default ElakaySwiperBlock;
